import { useEffect, useRef, useState } from 'react';
import { Col, Container, Row } from 'react-grid-system';
import styled from 'styled-components';
import { Button } from '../atoms';
import { useNavigate } from 'react-router';
import { pathURL } from '@/components/constants/urls';
import { useUserLanguage, useListNavigationStateContext } from '@/customHooks';
import { SupplierListTable, SupplierFilters, initialSupplierFilters } from '../organism';
import { SupplierFilterInput, useGetSuppliersQuery } from '@/graphql/generated';
import { Pagination } from '../molecules';

const textsData = {
  en: {
    title: 'Suppliers',
    buttonText: '+ Add Supplier'
  },
  es: {
    title: 'Proveedores',
    buttonText: '+ Agregar Proveedor'
  }
}

const ButtonContainer = styled(Col)`
  display: flex;
  align-items: center;
`

export const SupplierListPage = () => {
  const userLanguage = useUserLanguage()
  const texts = userLanguage === 'es' ? textsData.es : textsData.en

  const navigate = useNavigate()
  const { getListState, setListState } = useListNavigationStateContext()
  const hasRestoredState = useRef(false)

  useEffect(() => {
    if (!hasRestoredState.current) {
      const savedState = getListState('suppliers')
      if (savedState) {
        setCurrentPage(savedState.currentPage)
        setFilters(savedState.filters as SupplierFilterInput)
      }
      hasRestoredState.current = true
    }
  }, [getListState])

  const [currentPage, setCurrentPage] = useState(() => {
    const savedState = getListState('suppliers')
    return savedState?.currentPage ?? 1
  });
  const [filters, setFilters] = useState<SupplierFilterInput>(() => {
    const savedState = getListState('suppliers')
    return (savedState?.filters as SupplierFilterInput) ?? initialSupplierFilters
  });

  useEffect(() => {
    if (hasRestoredState.current) {
      setListState('suppliers', {
        currentPage,
        filters,
      })
    }
  }, [currentPage, filters, setListState])

  const handleFilterChange = (newFilters: SupplierFilterInput) => {
    setFilters(newFilters);
    setCurrentPage(1); // Reset to page 1 when filters change
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    setFilters({ ...filters, page: page.toString() });
  };

  const { data } = useGetSuppliersQuery({
    variables: {
      filterInput: { ...filters, page: currentPage.toString() },
    },
    fetchPolicy: 'network-only',
  });

  const currentDataCount = data?.suppliers?.items?.length || 0;
  const totalPages = data?.suppliers?.pagination?.totalPages || 1;
  const showPagination = currentDataCount > 0;

  return (
    <Container fluid>
      <Row>
        <Col>
            <h2>{texts.title}</h2>
        </Col>
        <ButtonContainer xs="content">
          <Button
            onClick={() => navigate(`/${pathURL.USER}/${pathURL.SUPPLIER}/${pathURL.CREATE}`)}
          >
            {texts.buttonText}
          </Button>
        </ButtonContainer>
      </Row>
      <Row>
        <Col>
          <SupplierFilters filters={filters} onFilterChange={handleFilterChange} />
        </Col>
      </Row>
      <Row>
        <Col>
          <SupplierListTable filters={{ ...filters, page: currentPage.toString() }} />
        </Col>
      </Row>
      {showPagination && (
        <Row>
          <Col>
            <Pagination 
              currentPage={currentPage} 
              onPageChange={handlePageChange} 
              currentDataCount={currentDataCount}
              pageSize={filters.pageSize || null}
              totalPages={totalPages}
            />
          </Col>
        </Row>
      )}
    </Container>
  )
}